import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { get, getOr, keys, pick, last, isEmpty } from 'lodash/fp';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { DragDrop } from '@uppy/react';
import {
  Schema,
  Button,
  FormControl,
  Form,
  Icon,
  Progress,
  Tag,
  IconButton,
  SelectPicker,
  ButtonToolbar,
  RadioGroup,
  Radio
} from 'rsuite';
import { fetchROItemAssocPicklistCount } from '../../../redux/Reservation/sagas/routines';
import { getROItemPicklistCount } from '../../../redux/Reservation/selectors/routes/ro-item/edit';
import uppyAwsS3Upload from '../../../helpers/uppyAwsS3Upload';
import toInventoryItemUniqKey from '../../../helpers/toInventoryItemUniqKey';
import inventoryItemUniqKeyToIds from '../../../helpers/inventoryItemUniqKeyToIds';
import Image from '../Image';
import { AppConfig } from '../../../helpers/config';
import { ControlLabel, FormGroup } from '../../widgets/FormElements';
import HealthLevelOptions from '../../../constants/common/HealthLevelOptions';
import ConfirmationModal from '../../ConfirmationModal';
import InventoryAvailabilitySelectPicker from '../../InventoryAvailabilitySelectPicker';

const { Line: ProgressLine } = Progress;
const { NumberType, StringType, BooleanType } = Schema.Types;

const FormModel = Schema.Model({
  name: StringType()
    .isRequired('This field is required')
    .minLength(2, 'Must be at least 2 characters long')
    .maxLength(120, 'Must not exceed 120 characters long')
    .addRule(
      value => !/<[a-z][\s\S]*>/i.test(value),
      'HTML tags are not allowed'
    ),
  description: StringType()
    .minLength(2, 'Must be at least 2 characters long')
    .maxLength(255, 'Must not exceed 255 characters long')
    .addRule(
      value => !/<[a-z][\s\S]*>/i.test(value),
      'HTML tags are not allowed'
    ),
  healthLevel: NumberType().range(0, 3, 'Values must 0 - 3'),
  isVisible: BooleanType(),
  inventoryItemUniqKey: StringType()
});

const PhotoFormGroup = styled(FormGroup)`
  .uppy-DragDrop-inner {
    padding: 10px 20px;
  }
`;

const FileTag = styled(Tag)`
  margin-top: 8px;
  margin-right: 20px;
`;

const ImageWrapper = styled.div`
  border-radius: 5px;
  position: relative;
  display: inline-block;
  border: 1px solid;
  border-color: #adadad;
  width: 300px;
  margin-top: 10px;
  padding: 3px;
`;

const RemoveImageIcon = styled(IconButton)`
  top: -10px;
  right: -12px;
  position: absolute !important;
`;

const defaultFormValue = {
  name: '',
  description: '',
  photo: null,
  healthLevel: 0,
  isVisible: true,
  inventoryItemUniqKey: ''
};

const dataToFormValue = ({
  inventoryItemId = null,
  inventoryLocationId = null,
  inventoryLocationZoneId = null,
  ...data
}) => {
  const formData = pick(keys(defaultFormValue), data);
  if (inventoryItemId) {
    const inventoryItemUniqKey = toInventoryItemUniqKey(
      inventoryItemId,
      inventoryLocationId,
      inventoryLocationZoneId
    );
    return { inventoryItemUniqKey, ...formData };
  }
  return formData;
};

const formValueToBody = ({ inventoryItemUniqKey, ...formValue }) => {
  const {
    inventoryItemId,
    inventoryLocationId,
    inventoryLocationZoneId
  } = inventoryItemUniqKeyToIds(inventoryItemUniqKey);

  return {
    inventoryItemId,
    inventoryLocationId,
    inventoryLocationZoneId,
    ...formValue
  };
};

const ROItemForm = ({ data, isSaving, isSaved, onSubmit }) => {
  const formRef = useRef();
  const dispatch = useDispatch();
  const isCreate = !get('id', data);
  const [formError, setFormError] = useState({});
  const [formValue, setFormValue] = useState(defaultFormValue);
  const [showConfirm, setShowConfirm] = useState(false);
  const [photo, setPhoto] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [prevIsVisible, setPrevIsVisible] = useState(null);
  const isUploading = uploadFile && uploadProgress && uploadProgress < 100;
  const savingInProgress =
    isSaving || isUploading || (uploadProgress === 100 && !isSaved);

  const picklistCount = useSelector(getROItemPicklistCount);
  const uppyUpload = useRef(
    uppyAwsS3Upload({
      type: 'image',
      bucket: get('buckets.reservations', AppConfig),
      onFileAdded: ({ data: { name } }) => {
        setUploadFile(name);
      },
      onUpload: () => {
        setUploadProgress(true);
      },
      onUploadProgress: (_, { bytesUploaded, bytesTotal }) => {
        setUploadProgress((100 * bytesUploaded) / bytesTotal);
      },
      onUploadError: () => {
        uppyUpload.current.reset();
        setUploadFile(null);
        setUploadProgress(null);
      },
      onComplete: result => {
        const photoUrl = get('successful.0.uploadURL', result);
        if (photoUrl) {
          const photoFilename = last(photoUrl.split('/'));
          setPhoto(photoFilename);
        }
      }
    })
  );

  useEffect(() => {
    if (data) {
      setFormValue(dataToFormValue(data));
      setPrevIsVisible(getOr(true, 'isVisible', data));
      dispatch(fetchROItemAssocPicklistCount(get('id', data)));
    }

    return () => {
      uppyUpload.current.close();
    };
  }, []);

  useEffect(
    () => {
      if (photo) {
        setFormValue({ ...formValue, photo });
        onSubmit(formValueToBody({ ...formValue, photo }));
      }
      return () => {
        setPhoto(null);
      };
    },
    [photo]
  );

  const handleFormChange = value => {
    setFormValue(value);
  };

  const handleIsVisibleChange = isVisible => {
    setFormValue({ ...formValue, isVisible });
  };

  const handleFormCheck = errors => {
    setFormError(errors);
  };

  const resetFileUpload = () => {
    uppyUpload.current.reset();
    setUploadFile(null);
    setUploadProgress(null);
  };

  const handleSubmit = () => {
    if (!formRef.current.check()) {
      return;
    }
    if (uploadFile && uploadProgress === null) {
      uppyUpload.current.upload();
      return;
    }

    onSubmit(formValueToBody(formValue));
  };

  const handleReset = () => {
    setFormValue(isCreate ? defaultFormValue : data);
    resetFileUpload();
  };

  const handleRemovePhoto = () => {
    setFormValue({ ...formValue, photo: null });
  };

  const renderUploadProgress = () => {
    const status = uploadProgress < 100 ? 'active' : 'success';
    return (
      <ProgressLine
        percent={Math.round(uploadProgress)}
        status={status}
        style={{ width: 320, float: 'right', marginRight: 100 }}
      />
    );
  };

  const renderPhoto = () => {
    if (uploadFile && !formValue.photo) {
      return (
        <FileTag onClick={resetFileUpload} closable>
          {uploadFile}
        </FileTag>
      );
    }

    return (
      formValue.photo && (
        <ImageWrapper>
          <RemoveImageIcon
            size="xs"
            icon={<Icon icon="close" />}
            onClick={handleRemovePhoto}
          />
          <Image
            alt="photo"
            image={formValue.photo}
            style={{ borderRadius: 5 }}
          />
        </ImageWrapper>
      )
    );
  };

  const onCancelConfirm = () => {
    setShowConfirm(false);
  };

  const onShowConfirm = () => {
    // Show confirm only if in Picklist and isVisible value was changed
    if (picklistCount > 0 && prevIsVisible !== get('isVisible', formValue)) {
      setShowConfirm(true);
    } else {
      handleSubmit();
    }
  };

  return (
    <Form
      ref={formRef}
      model={FormModel}
      layout="horizontal"
      formValue={formValue}
      formError={formError}
      onCheck={handleFormCheck}
      onChange={handleFormChange}
      style={{ maxWidth: 600 }}
    >
      <FormGroup>
        <ControlLabel>Name *</ControlLabel>
        <FormControl
          name="name"
          errorMessage={formError.name}
          placeholder="Item Name"
        />
      </FormGroup>
      <FormGroup>
        <ControlLabel>Description</ControlLabel>
        <FormControl
          name="description"
          rows={3}
          componentClass="textarea"
          errorMessage={formError.description}
        />
      </FormGroup>

      <FormGroup>
        <ControlLabel>Link to Inventory</ControlLabel>
        <FormControl
          name="inventoryItemUniqKey"
          accepter={InventoryAvailabilitySelectPicker}
          style={{ width: 300, marginTop: 10 }}
        />
      </FormGroup>

      <PhotoFormGroup>
        <ControlLabel>Photo</ControlLabel>
        {formValue.photo || uploadFile ? (
          renderPhoto()
        ) : (
          <DragDrop
            width="300px"
            height="130px"
            note={get('current.opts.note', uppyUpload)}
            uppy={uppyUpload.current}
          />
        )}
        {uploadProgress && renderUploadProgress()}
      </PhotoFormGroup>
      <FormGroup>
        <ControlLabel>Stop Light (Health Indicator)</ControlLabel>
        <FormControl
          name="healthLevel"
          accepter={SelectPicker}
          style={{ width: 300, marginTop: 10 }}
          data={HealthLevelOptions}
        />
      </FormGroup>
      <FormGroup controlId="isVisible">
        <ControlLabel>Status</ControlLabel>
        <RadioGroup
          inline
          name="isVisible"
          value={formValue.isVisible}
          onChange={handleIsVisibleChange}
        >
          <Radio value>Show</Radio>
          <Radio value={false}>Hide</Radio>
        </RadioGroup>
      </FormGroup>
      <ButtonToolbar
        style={{ textAlign: 'right', marginTop: 30, marginRight: 110 }}
      >
        <Button
          onClick={handleReset}
          appearance="default"
          disabled={savingInProgress}
        >
          Reset
        </Button>
        <Button
          onClick={onShowConfirm}
          appearance="primary"
          disabled={savingInProgress || !isEmpty(formError)}
        >
          <Icon icon="save" /> {savingInProgress ? 'Saving...' : 'Save'}
        </Button>
      </ButtonToolbar>
      <ConfirmationModal
        show={showConfirm}
        title="Confirm Changes"
        onCancel={onCancelConfirm}
        onConfirm={handleSubmit}
        cancelButtonLabel="No"
        confirmButtonLabel="Yes"
      >
        Item is present in one or more picklists. Hiding/Showing an item will
        also change the visibility of the item in your picklist/s. Would you
        like to continue?
      </ConfirmationModal>
    </Form>
  );
};

ROItemForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  isSaving: PropTypes.bool.isRequired,
  isSaved: PropTypes.bool.isRequired,
  data: PropTypes.shape({})
};

ROItemForm.defaultProps = {
  data: null
};

export default ROItemForm;
